import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './FeedbackDialog.css';
import { FRONTEND_VERSION } from '../config/version';

const DRAFT_KEY = 'stoppuhr_feedback_draft';

const FeedbackDialog = ({
  isOpen,
  onClose,
  onSubmit,
  backendUrl,
  userEmail,
  frontendVersion,
  backendVersion,
  licenseKey,
  deviceId = null,
  deviceName = null,
  isSubmitting = false,
  backendAvailable = null,
  darkMode = false,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState('BUG');
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  
  // Speech Recognition States
  const [isRecordingTitle, setIsRecordingTitle] = useState(false);
  const [isRecordingDescription, setIsRecordingDescription] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const currentRecordingFieldRef = useRef(null); // 'title' oder 'description'

  // SpeechRecognition-Initialisierung
  useEffect(() => {
    // Prüfe Browser-Kompatibilität
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.log('[SpeechRecognition] Nicht unterstützt in diesem Browser');
      setSpeechSupported(false);
      return;
    }

    setSpeechSupported(true);
    
    // Erstelle SpeechRecognition-Instanz
    const recognition = new SpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = false; // Stoppt automatisch nach Pause
    recognition.interimResults = false; // Nur finale Ergebnisse
    
    // Event-Handler für Ergebnisse
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join(' ');
      
      handleSpeechResult(transcript);
    };
    
    // Event-Handler für Fehler
    recognition.onerror = (event) => {
      console.error('[SpeechRecognition] Fehler:', event.error);
      if (event.error === 'no-speech') {
        // Keine Sprache erkannt - normal, wenn Benutzer nichts sagt
        console.log('[SpeechRecognition] Keine Sprache erkannt');
      } else if (event.error === 'not-allowed') {
        alert(t('alerts.microphone_denied'));
      } else {
        alert(t('alerts.speech_input_error', { msg: event.error }));
      }
      stopRecording();
    };
    
    // Event-Handler für Ende der Aufnahme
    recognition.onend = () => {
      stopRecording();
    };
    
    recognitionRef.current = recognition;
    
    // Cleanup beim Unmount
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignoriere Fehler beim Stoppen
        }
      }
    };
  }, [t]);

  // Beim Öffnen: gespeicherten Entwurf (noch nicht gesendetes Feedback) laden
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && typeof d === 'object') {
          if (d.type && ['BUG', 'IDEA', 'FEATURE_REQUEST', 'OTHER'].includes(d.type)) setType(d.type);
          if (d.category != null) setCategory(String(d.category));
          if (d.title != null) setTitle(String(d.title));
          if (d.description != null) setDescription(String(d.description));
          if (Array.isArray(d.images) && d.images.length > 0) {
            setImages(d.images);
            if (Array.isArray(d.imagePreviews) && d.imagePreviews.length === d.images.length) {
              setImagePreviews(d.imagePreviews);
            } else {
              setImagePreviews(d.images.map(img => `data:${img.mime_type || 'image/jpeg'};base64,${img.file_data || ''}`));
            }
          } else {
            setImages([]);
            setImagePreviews([]);
          }
        }
      }
    } catch (e) {
      // Fehler beim Lesen ignorieren
    }
  }, [isOpen]);

  // Schließen: Entwurf speichern (damit beim nächsten Öffnen nichts verloren geht)
  const handleClose = () => {
    const hasContent = (title && title.trim()) || (description && description.trim()) || category || (images && images.length > 0);
    if (hasContent) {
      try {
        const draft = { type, category, title: title || '', description: description || '', images: images || [], imagePreviews: imagePreviews || [] };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch (e) {
        // Bei QuotaExceeded: nur Text ohne Bilder speichern
        try {
          const draft = { type, category, title: title || '', description: description || '', images: [], imagePreviews: [] };
          localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        } catch (e2) {}
      }
    } else {
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
    }
    onClose();
  };

  if (!isOpen) return null;

  // Komprimiere Bild (ähnlich wie in App.jsx)
  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      // Prüfe Dateigröße (max 10MB vor Komprimierung)
      if (file.size > 10 * 1024 * 1024) {
        reject(new Error('Bild ist zu groß (max. 10MB). Bitte wählen Sie ein kleineres Bild.'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (blob) {
              // Prüfe komprimierte Größe (max 5MB)
              if (blob.size > 5 * 1024 * 1024) {
                // Versuche mit niedrigerer Qualität
                canvas.toBlob((smallerBlob) => {
                  if (smallerBlob && smallerBlob.size <= 5 * 1024 * 1024) {
                    resolve(smallerBlob);
                  } else {
                    reject(new Error('Bild ist auch nach Komprimierung zu groß. Bitte wählen Sie ein kleineres Bild.'));
                  }
                }, 'image/jpeg', 0.6);
              } else {
                resolve(blob);
              }
            } else {
              reject(new Error('Fehler bei der Bildkomprimierung'));
            }
          }, 'image/jpeg', quality);
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newImages = [];
    const newPreviews = [];

    // Verarbeite alle Dateien sequenziell und warte auf jeden FileReader
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        
        // Warte auf FileReader mit Promise
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            resolve(event.target.result);
          };
          reader.onerror = () => {
            reject(new Error('Fehler beim Lesen der Datei'));
          };
          reader.readAsDataURL(compressed);
        });

        newImages.push({
          file_name: file.name,
          file_data: base64.split(',')[1], // Entferne data:image/jpeg;base64, Präfix
          file_size: compressed.size,
          mime_type: compressed.type || 'image/jpeg',
        });
        newPreviews.push(base64);
      } catch (error) {
        console.error('Fehler beim Komprimieren des Bildes:', error);
        alert(t('alerts.feedback_file_error', { file: file.name, msg: error.message }));
      }
    }

    // Aktualisiere State nur wenn Bilder verarbeitet wurden
    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
      setImagePreviews(prev => [...prev, ...newPreviews]);
    }

    // Reset file input, damit gleiche Datei erneut ausgewählt werden kann
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Speech Recognition Funktionen
  const handleSpeechResult = (transcript) => {
    const field = currentRecordingFieldRef.current;
    if (field === 'title') {
      setTitle(prev => prev ? `${prev} ${transcript}` : transcript);
    } else if (field === 'description') {
      setDescription(prev => prev ? `${prev} ${transcript}` : transcript);
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignoriere Fehler beim Stoppen
      }
    }
    setIsRecordingTitle(false);
    setIsRecordingDescription(false);
    currentRecordingFieldRef.current = null;
  };

  const startRecordingTitle = () => {
    if (!speechSupported || !recognitionRef.current) {
      alert(t('alerts.speech_not_supported'));
      return;
    }

    // Stoppe eventuelle laufende Aufnahme
    if (isRecordingTitle || isRecordingDescription) {
      stopRecording();
    }

    try {
      currentRecordingFieldRef.current = 'title';
      setIsRecordingTitle(true);
      recognitionRef.current.start();
    } catch (error) {
      console.error('[SpeechRecognition] Fehler beim Starten:', error);
      alert(t('alerts.speech_start_error'));
      stopRecording();
    }
  };

  const startRecordingDescription = () => {
    if (!speechSupported || !recognitionRef.current) {
      alert(t('alerts.speech_not_supported'));
      return;
    }

    // Stoppe eventuelle laufende Aufnahme
    if (isRecordingTitle || isRecordingDescription) {
      stopRecording();
    }

    try {
      currentRecordingFieldRef.current = 'description';
      setIsRecordingDescription(true);
      recognitionRef.current.start();
    } catch (error) {
      console.error('[SpeechRecognition] Fehler beim Starten:', error);
      alert(t('alerts.speech_start_error'));
      stopRecording();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert(t('alerts.feedback_title_required'));
      return;
    }

    if (!description.trim()) {
      alert(t('alerts.feedback_description_required'));
      return;
    }

    // Device-Info sammeln
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    };

    // Validiere, dass alle Bilder vollständig verarbeitet wurden
    if (images.length !== imagePreviews.length) {
      alert(t('alerts.feedback_wait_images'));
      return;
    }

    // Validiere, dass alle Bilder vollständige Daten haben
    const incompleteImages = images.filter(img => !img.file_data || img.file_data.length === 0);
    if (incompleteImages.length > 0) {
      alert(t('alerts.feedback_images_loading'));
      return;
    }

    const feedbackData = {
      user_email: userEmail,
      license_key: licenseKey || null,
      frontend_version: frontendVersion || FRONTEND_VERSION,
      backend_version: backendVersion || null,
      type_: type,
      category: category || null,
      title: title.trim(),
      description: description.trim(),
      device_info: deviceInfo,
      device_id: deviceId || null,
      device_name: deviceName || null,
      images: images.length > 0 ? images : null,
    };

    console.log('[FeedbackDialog] Sende Feedback:', {
      ...feedbackData,
      images: images.length > 0 ? `${images.length} Bilder` : 'keine Bilder',
      imageSizes: images.map(img => `${img.file_name}: ${img.file_data?.length || 0} bytes`)
    });

    try {
      await onSubmit(feedbackData);
      // Entwurf löschen, da erfolgreich gesendet
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
      // Reset form
      setType('BUG');
      setCategory('');
      setTitle('');
      setDescription('');
      setImages([]);
      setImagePreviews([]);
      onClose();
    } catch (error) {
      console.error('[FeedbackDialog] Fehler beim Senden des Feedbacks:', error);
      // Lass den Fehler durch, damit handleFeedbackSubmit ihn behandeln kann
      throw error;
    }
  };

  const typeLabels = {
    BUG: t('feedback.type_bug'),
    IDEA: t('feedback.type_idea'),
    FEATURE_REQUEST: t('feedback.type_feature'),
    OTHER: t('feedback.type_other'),
  };

  return (
    <div className="feedback-dialog-overlay" onClick={handleClose}>
      <div 
        className={`feedback-dialog ${darkMode ? 'dark-mode' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: darkMode ? '#1f2937' : 'white',
          color: darkMode ? '#f9fafb' : '#111827'
        }}
      >
        <div 
          className="feedback-dialog-header"
          style={{
            borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
          }}
        >
          <h2 style={{ color: darkMode ? '#f9fafb' : '#1f2937' }}>{t('feedback.title')}</h2>
          <button 
            className="feedback-dialog-close" 
            onClick={handleClose}
            style={{
              color: darkMode ? '#9ca3af' : '#6b7280'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >×</button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-dialog-form">
          <div className="feedback-form-group">
            <label 
              htmlFor="feedback-type"
              style={{ color: darkMode ? '#f9fafb' : '#374151' }}
            >{t('feedback.type_label')}</label>
            <select
              id="feedback-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              required
              style={{
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                paddingRight: '2.5rem',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: darkMode 
                  ? "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")"
                  : "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = darkMode 
                  ? '0 0 0 3px rgba(59, 130, 246, 0.2)' 
                  : '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            >
              {Object.entries(typeLabels).map(([value, label]) => (
                <option 
                  key={value} 
                  value={value}
                  style={{
                    background: darkMode ? '#374151' : 'white',
                    color: darkMode ? '#f9fafb' : '#111827'
                  }}
                >{label}</option>
              ))}
            </select>
          </div>

          <div className="feedback-form-group">
            <label 
              htmlFor="feedback-category"
              style={{ color: darkMode ? '#f9fafb' : '#374151' }}
            >{t('feedback.category_label')}</label>
            <select
              id="feedback-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                paddingRight: '2.5rem',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: darkMode 
                  ? "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")"
                  : "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = darkMode 
                  ? '0 0 0 3px rgba(59, 130, 246, 0.2)' 
                  : '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option 
                value=""
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827'
                }}
              >{t('feedback.category_please_choose')}</option>
              <option 
                value="ERFASSUNG"
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827'
                }}
              >{t('feedback.category_erfassung')}</option>
              <option 
                value="GUI"
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827'
                }}
              >{t('feedback.category_gui')}</option>
              <option 
                value="FUNKTIONALITAET"
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827'
                }}
              >{t('feedback.category_funktionalitaet')}</option>
              <option 
                value="AUSWERTUNG"
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827'
                }}
              >{t('feedback.category_auswertung')}</option>
              <option 
                value="SONSTIGES"
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827'
                }}
              >{t('feedback.category_sonstiges')}</option>
            </select>
          </div>

          <div className="feedback-form-group">
            <label 
              htmlFor="feedback-title"
              style={{ color: darkMode ? '#f9fafb' : '#374151' }}
            >{t('feedback.title_label')}</label>
            <div className="feedback-input-with-mic">
              <input
                id="feedback-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('feedback.title_placeholder')}
                required
                maxLength={500}
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = darkMode 
                    ? '0 0 0 3px rgba(59, 130, 246, 0.2)' 
                    : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {speechSupported && (
                <button
                  type="button"
                  onClick={isRecordingTitle ? stopRecording : startRecordingTitle}
                  className={`feedback-mic-btn ${isRecordingTitle ? 'recording' : ''}`}
                  title={isRecordingTitle ? 'Aufnahme stoppen' : 'Spracheingabe starten'}
                  style={{
                    background: isRecordingTitle 
                      ? '#fee2e2' 
                      : (darkMode ? '#374151' : '#f3f4f6'),
                    border: `1px solid ${isRecordingTitle ? '#ef4444' : (darkMode ? '#4b5563' : '#d1d5db')}`,
                    color: darkMode ? '#f9fafb' : '#111827'
                  }}
                  onMouseEnter={(e) => {
                    if (!isRecordingTitle) {
                      e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isRecordingTitle) {
                      e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                    }
                  }}
                >
                  {isRecordingTitle ? '⏹️' : '🎤'}
                </button>
              )}
            </div>
            {isRecordingTitle && (
              <div 
                className="feedback-recording-indicator"
                style={{ color: darkMode ? '#fca5a5' : '#dc2626' }}
              >
                <span 
                  className="feedback-recording-dot"
                  style={{ background: darkMode ? '#fca5a5' : '#dc2626' }}
                ></span>
                {t('feedback.recording')}
              </div>
            )}
          </div>

          <div className="feedback-form-group">
            <label 
              htmlFor="feedback-description"
              style={{ color: darkMode ? '#f9fafb' : '#374151' }}
            >{t('feedback.description_label')}</label>
            <div className="feedback-textarea-with-mic">
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('feedback.description_placeholder')}
                required
                rows={6}
                style={{
                  background: darkMode ? '#374151' : 'white',
                  color: darkMode ? '#f9fafb' : '#111827',
                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = darkMode 
                    ? '0 0 0 3px rgba(59, 130, 246, 0.2)' 
                    : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = darkMode ? '#4b5563' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {speechSupported && (
                <button
                  type="button"
                  onClick={isRecordingDescription ? stopRecording : startRecordingDescription}
                  className={`feedback-mic-btn ${isRecordingDescription ? 'recording' : ''}`}
                  title={isRecordingDescription ? 'Aufnahme stoppen' : 'Spracheingabe starten'}
                  style={{
                    background: isRecordingDescription 
                      ? '#fee2e2' 
                      : (darkMode ? '#374151' : '#f3f4f6'),
                    border: `1px solid ${isRecordingDescription ? '#ef4444' : (darkMode ? '#4b5563' : '#d1d5db')}`,
                    color: darkMode ? '#f9fafb' : '#111827'
                  }}
                  onMouseEnter={(e) => {
                    if (!isRecordingDescription) {
                      e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isRecordingDescription) {
                      e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                    }
                  }}
                >
                  {isRecordingDescription ? '⏹️' : '🎤'}
                </button>
              )}
            </div>
            {isRecordingDescription && (
              <div 
                className="feedback-recording-indicator"
                style={{ color: darkMode ? '#fca5a5' : '#dc2626' }}
              >
                <span 
                  className="feedback-recording-dot"
                  style={{ background: darkMode ? '#fca5a5' : '#dc2626' }}
                ></span>
                {t('feedback.recording')}
              </div>
            )}
          </div>

          <div className="feedback-form-group">
            <label style={{ color: darkMode ? '#f9fafb' : '#374151' }}>{t('feedback.images_label')}</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="feedback-add-image-btn"
              style={{
                background: darkMode ? '#374151' : '#f3f4f6',
                color: darkMode ? '#f9fafb' : '#111827',
                border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
              }}
            >
              📷 {t('feedback.add_image')}
            </button>

            {imagePreviews.length > 0 && (
              <div className="feedback-image-previews">
                {imagePreviews.map((preview, index) => (
                  <div 
                    key={index} 
                    className="feedback-image-preview"
                    style={{
                      border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
                    }}
                  >
                    <img src={preview} alt={`Vorschau ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="feedback-remove-image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div 
            className="feedback-dialog-actions"
            style={{
              borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
            }}
          >
            <button 
              type="button" 
              onClick={handleClose} 
              disabled={isSubmitting}
              style={{
                background: darkMode ? '#374151' : '#f3f4f6',
                color: darkMode ? '#f9fafb' : '#374151'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                }
              }}
            >
              {t('dialog.cancel')}
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting || !title.trim() || !description.trim()}
              style={{
                background: isSubmitting || !title.trim() || !description.trim() 
                  ? (darkMode ? '#4b5563' : '#9ca3af')
                  : '#3b82f6',
                color: 'white'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && title.trim() && description.trim()) {
                  e.target.style.background = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && title.trim() && description.trim()) {
                  e.target.style.background = '#3b82f6';
                }
              }}
            >
              {isSubmitting 
                ? (backendAvailable === true ? t('feedback.sending') : t('feedback.saving'))
                : (backendAvailable === true ? t('feedback.submit') : t('feedback.save'))
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackDialog;
